// Types for the internal-admin `/private/tenants` super-admin API. This surface is not part of
// the OpenAPI spec that generates @onefootprint/request-types, so these types live in the app.

export type GetTenantsRequest = {
  search?: string;
  page_size?: string;
};

export type GetTenantsResponse = Tenant[];

export enum TenantSupportedAuthMethod {
  GoogleOauth = 'google_oauth',
  MagicLink = 'magic_link',
  SamlSso = 'saml_sso',
}

export enum TenantPreviewApi {
  MatchSignalsList = 'match_signals_list',
  LivenessList = 'liveness_list',
  AuthEventsList = 'auth_events_list',
  IdentityDocumentConsent = 'identity_document_consent',
  DocumentsList = 'documents_list',
  RiskSignalsList = 'risk_signals_list',
  VaultIntegrity = 'vault_integrity',
  CreateUserDecision = 'create_user_decision',
  CreateBusinessOwner = 'create_business_owner',
  ListBusinessOwners = 'list_business_owners',
  Labels = 'labels',
  Tags = 'tags',
  VaultProxy = 'vault_proxy',
  VaultProxyJit = 'vault_proxy_jit',
  VaultVersioning = 'vault_versioning',
  OnboardingsList = 'onboardings_list',
  OnboardingsSandboxFixture = 'onboardings_sandbox_fixture',
  DecisionsList = 'decisions_list',
  ImplicitAuth = 'implicit_auth',
  VaultDisasterRecovery = 'vault_disaster_recovery',
  LegacyOnboardingStatusWebhook = 'legacy_onboarding_status_webhook',
  LegacyListUsersBusinesses = 'legacy_list_users_businesses',
  ListDuplicateUsers = 'list_duplicate_users',
  SoftDeleteUsers = 'soft_delete_users',
  PostKycStepupLinks = 'post_kyc_stepup_links',
  SmsLinkAuthentication = 'sms_link_authentication',
  AuthlessPlaybooks = 'authless_playbooks',
  BankLinking = 'bank_linking',
  HeadlessDocumentData = 'headless_document_data',
  BusinessInsightsApi = 'business_insights_api',
  ProgrammableOnboarding = 'programmable_onboarding',
  AiContext = 'ai_context',
  DeviceInsights = 'device_insights',
  ThirdPartyAuth = 'third_party_auth',
  TenantConsent = 'tenant_consent',
}

export enum TenantBillingProfileProduct {
  monthlyPlatformFee = 'monthly_platform_fee',
  kyc = 'kyc',
  oneClickKyc = 'one_click_kyc',
  kycWaterfallSecondVendor = 'kyc_waterfall_second_vendor',
  kycWaterfallThirdVendor = 'kyc_waterfall_third_vendor',
  idDocs = 'id_docs',
  kyb = 'kyb',
  kybEinOnly = 'kyb_ein_only',
  curpVerification = 'curp_verification',
  pii = 'pii',
  hotVaults = 'hot_vaults',
  hotProxyVaults = 'hot_proxy_vaults',
  vaultsWithNonPci = 'vaults_with_non_pci',
  vaultsWithPci = 'vaults_with_pci',
  watchlistChecks = 'watchlist_checks',
  adverseMediaPerOnboarding = 'adverse_media_per_onboarding',
  adverseMediaPerYear = 'adverse_media_per_year',
  continuousMonitoringPerYear = 'continuous_monitoring_per_year',
  sambaActivityHistory = 'samba_activity_history',
  neuroIdBehavioral = 'neuro_id_behavioral',
  sentilinkScore = 'sentilink_score',
  bankLinkAccountNumbers = 'bank_link_account_numbers',
  bankLinkBalanceTransactions = 'bank_link_balance_transactions',
  bankLinkIdentity = 'bank_link_identity',
  bankLinkConnections = 'bank_link_connections',
  vaultDisasterRecovery = 'vault_disaster_recovery',
}

export enum TenantFeatureGate {
  AiWatchlistReviewNode = 'ai_watchlist_review_node',
  AiDocumentExtractionNode = 'ai_document_extraction_node',
  AiResearchNode = 'ai_research_node',
  AiCustomDecisionNode = 'ai_custom_decision_node',
  AiSocialDecisionNodeNode = 'ai_social_decision_node_node',
  AiAdverseMediaDecisionNode = 'ai_adverse_media_decision_node',
  AiBusinessResearchDecisionNode = 'ai_business_research_decision_node',
  VerifiedPrefill = 'verified_prefill',
  CodeExecution = 'code_execution',
  Reporting = 'reporting',
  Copilot = 'copilot',
  SmsOtp = 'sms_otp',
  EmailOtp = 'email_otp',
  Agents = 'agents',
  AgentVaultDocuments = 'agent_vault_documents',
  AgentDecisionNode = 'agent_decision_node',
  PercyChat = 'percy_chat',
  PlaybookOutput = 'playbook_output',
  Incode = 'incode',
  ManagedVendors = 'managed_vendors',
  McpApiKey = 'mcp_api_key',
  SelfieNode = 'selfie_node',
  AppClipSelfie = 'app_clip_selfie',
}

/**
 * The price of each product for this tenant, set in cents.
 */
export type TenantBillingProfile = {
  prices: TenantBillingProfilePrices;
  billingEmail?: string;
  pricingDoc?: string;
  minimums: BillingMinimum[];
  platformFeeStartsOn?: string;
  omitBilling: boolean;
  sendAutomatically: boolean;
  orbSubscriptionId?: string;
  trialDurationDays?: number;
};

export type BillingMinimum = {
  products: TenantBillingProfileProduct[];
  amountCents: string;
  name: string;
  startsOn?: string | null;
};

export type TenantBillingProfilePrices = Partial<Record<TenantBillingProfileProduct, string | null>>;

export type TenantVendorControl = {
  idologyEnabled: boolean;
  experianEnabled: boolean;
  lexisEnabled: boolean;
  experianSubscriberCode?: string;
  middeskApiKeyExists: boolean;
  sentilinkCredentialsExists: boolean;
  taktileDetail?: TaktileDetail;
  neuroEnabled: boolean;
  verifiedApiKeyExists: boolean;
};
export type TaktileDetail = {
  organizationName: string;
};

export type TenantBusinessInfo = {
  companyName: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
};

export type Tenant = {
  id: string;
  name: string;
  domains: string[];
  allowDomainAccess: boolean;
  isLive: boolean;
  supportedAuthMethods: string[] | null;
  numPlaybooks: number;
  createdAt: string;
  superTenantId: string | null;
  businessInfo?: TenantBusinessInfo;
};

export type TenantDetail = {
  id: string;
  name: string;
  createdAt: string;

  domains: string[];
  allowDomainAccess: boolean;

  sandboxRestricted: boolean;

  supportedAuthMethods?: TenantSupportedAuthMethod[];
  // Plain strings because the backend also returns values missing from `TenantPreviewApi` and
  // `TenantFeatureGate`, such as ones it has since removed
  allowedPreviewApis: string[];
  featureGates: string[];
  pinnedApiVersion?: number;
  isDemoTenant: boolean;

  superTenantId?: string;
  workosId?: string;
  stripeCustomerId?: string;
  appClipExperienceId: string;

  billingProfile?: TenantBillingProfile;
  vendorControl?: TenantVendorControl;
  businessInfo?: TenantBusinessInfo;

  parentTenant?: TenantHierarchy;
  childTenants?: TenantHierarchy[];
};

export type TenantHierarchy = {
  id: string;
  name: string;
};

export type OrbCustomerDetails = {
  name: string;
  billingEmail: string;
  autoCollection: boolean;
  emailDelivery: boolean;
  invoiceViaStripe: boolean;
  dashboardUrl?: string;
  portalUrl?: string;
  activeSubscriptions: OrbSubscriptionDetails[];
  parentCustomerId?: string;
  childCustomerIds?: string[];
};

export type OrbSubscriptionDetails = {
  id: string;
  startDate: string;
  trialEndDate?: string | null;
  autoCollection?: boolean | null;
  lineItems: OrbSubscriptionLineItem[];
  adjustments: OrbSubscriptionAdjustment[];
  platformFee?: OrbSubscriptionAdjustment | null;
};

export type OrbSubscriptionLineItem = {
  id: string;
  name: string;
  unitAmount?: string | null;
  tiers: OrbPriceTier[];
};

export type OrbPriceTier = {
  firstUnit: string;
  lastUnit?: string | null;
  unitAmount: string;
};

export type OrbSubscriptionAdjustment = {
  id: string;
  startDate: string;
  amount?: string | null;
  kind: string;
  reason?: string | null;
};
